// frontend/src/admin/blockforms/FlowchartForm.js — FULL REPLACEMENT (Phase D, Plan B)
// Browser-side SVG capture: the admin's own browser renders the mermaid code
// to SVG at save time (npm 'mermaid' package, dynamically imported so the
// student bundle is unaffected), uploads it through the EXISTING
// /rich-content/upload-image endpoint, and stores svg_url per language:
//   content_data = { en: {mermaid_code, caption, svg_url}, hi: {...} }
// Students load the static SVG (fast, cacheable, no CDN); the mermaid code
// stays saved for future edits and AI context.
//
// Behavior change vs old form: invalid mermaid code now BLOCKS save with the
// mermaid error shown — better than silently saving a chart that would render
// broken for students. A Preview button renders the chart inline before save.

import React, { useState, useEffect } from 'react';
import { buildBlockSave } from './blockSaveTarget';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://80.225.197.31:8001';

let mermaidInstance = null;
const getMermaid = async () => {
    if (!mermaidInstance) {
        const mod = await import('mermaid');
        mermaidInstance = mod.default;
        mermaidInstance.initialize({
            startOnLoad: false,
            theme: 'default',
            flowchart: { useMaxWidth: false, htmlLabels: false, nodeSpacing: 30, rankSpacing: 40 },
            themeVariables: { fontSize: '20px' }
        });
    }
    return mermaidInstance;
};

const renderToSvg = async (code) => {
    const mermaid = await getMermaid();
    const id = 'fc' + Math.random().toString(36).slice(2);
    const { svg } = await mermaid.render(id, code);

    // Crop the SVG canvas to the actual drawing (fixes oversized empty canvas)
    const holder = document.createElement('div');
    holder.style.cssText = 'position:absolute;left:-99999px;top:0;';
    holder.innerHTML = svg;
    document.body.appendChild(holder);
    try {
        const svgEl = holder.querySelector('svg');
        const bbox = svgEl.getBBox();
        const pad = 10;
        svgEl.setAttribute('viewBox', `${bbox.x - pad} ${bbox.y - pad} ${bbox.width + pad * 2} ${bbox.height + pad * 2}`);
        svgEl.setAttribute('width', Math.ceil(bbox.width + pad * 2));
        svgEl.setAttribute('height', Math.ceil(bbox.height + pad * 2));
        svgEl.removeAttribute('style');
        return new XMLSerializer().serializeToString(svgEl);
    } finally {
        document.body.removeChild(holder);
    }
};

const uploadSvg = async (svgString) => {
    const file = new File([svgString], 'flowchart.svg', { type: 'image/svg+xml' });
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${API_BASE}/rich-content/upload-image`, { method: 'POST', body: fd });
    if (!res.ok) throw new Error('SVG upload failed');
    const data = await res.json();
    return data.file_path;
};

const FlowchartForm = ({ level, entityId, section, editingBlock, nextOrderIndex, onSaved, onCancel }) => {
    const existing = editingBlock?.content_data || {};

    const [mermaidEn, setMermaidEn] = useState(existing.en?.mermaid_code || '');
    const [mermaidHi, setMermaidHi] = useState(existing.hi?.mermaid_code || '');
    const [captionEn, setCaptionEn] = useState(existing.en?.caption || '');
    const [captionHi, setCaptionHi] = useState(existing.hi?.caption || '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [previewSvg, setPreviewSvg] = useState(null);

    useEffect(() => {
        const handleBeforeUnload = (e) => {
            e.preventDefault();
            e.returnValue = '';
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [mermaidEn, mermaidHi, captionEn, captionHi]);

    const handlePreview = async (code) => {
        setError(null);
        setPreviewSvg(null);
        if (!code.trim()) return;
        try {
            setPreviewSvg(await renderToSvg(code));
        } catch (err) {
            setError(`Mermaid error: ${err.message || err}`);
        }
    };

    const handleSave = async () => {
        if (!mermaidEn.trim()) {
            setError('Please enter Mermaid diagram code (English)');
            return;
        }
        setError(null);
        setSaving(true);

        try {
            // 1. Render + upload English SVG (skip re-render if code unchanged)
            let svgUrlEn = existing.en?.svg_url || null;
            if (!svgUrlEn || existing.en?.mermaid_code !== mermaidEn) {
                let svgEn;
                try {
                    svgEn = await renderToSvg(mermaidEn);
                } catch (err) {
                    throw new Error(`English mermaid code is invalid — fix it before saving. (${err.message || err})`);
                }
                svgUrlEn = await uploadSvg(svgEn);
            }

            // 2. Hindi (optional)
            let svgUrlHi = existing.hi?.svg_url || null;
            if (mermaidHi.trim()) {
                if (!svgUrlHi || existing.hi?.mermaid_code !== mermaidHi) {
                    let svgHi;
                    try {
                        svgHi = await renderToSvg(mermaidHi);
                    } catch (err) {
                        throw new Error(`Hindi mermaid code is invalid — fix it before saving. (${err.message || err})`);
                    }
                    svgUrlHi = await uploadSvg(svgHi);
                }
            } else {
                svgUrlHi = null;
            }

            // 3. Save block
            const content_data = {
                en: { mermaid_code: mermaidEn, caption: captionEn, svg_url: svgUrlEn }
            };
            if (mermaidHi.trim() || captionHi) {
                content_data.hi = { mermaid_code: mermaidHi, caption: captionHi, svg_url: svgUrlHi };
            }

            const { url, method, payload } = buildBlockSave({
                level, entityId, section, editingBlock,
                blockType: 'flowchart',
                contentData: content_data,
                orderIndex: nextOrderIndex,
            });

            const res = await fetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (!res.ok) {
                const err = await res.json();
                throw new Error(err.detail || 'Save failed');
            }
            onSaved();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="ace-form">
            <h4>{editingBlock ? 'Edit' : 'Add'} Flowchart</h4>
            {error && <div className="ace-error">{error}</div>}

            <label>Mermaid Code (English) *</label>
            <textarea rows={6} value={mermaidEn} onChange={(e) => setMermaidEn(e.target.value)} placeholder="graph TD&#10;A[Start] --> B[End]" />
            <button type="button" onClick={() => handlePreview(mermaidEn)} style={{ marginBottom: 8 }}>Preview (EN)</button>
            {existing.en?.svg_url && <p style={{ fontSize: '0.78rem', color: '#059669', margin: '0 0 8px' }}>✓ SVG exists: {existing.en.svg_url}</p>}

            <label>Caption (English)</label>
            <input type="text" value={captionEn} onChange={(e) => setCaptionEn(e.target.value)} />

            <label>Mermaid Code (Hindi, optional)</label>
            <textarea rows={6} value={mermaidHi} onChange={(e) => setMermaidHi(e.target.value)} />
            <button type="button" onClick={() => handlePreview(mermaidHi)} style={{ marginBottom: 8 }}>Preview (HI)</button>

            <label>Caption (Hindi, optional)</label>
            <input type="text" value={captionHi} onChange={(e) => setCaptionHi(e.target.value)} />

            {previewSvg && (
                <div className="fc-preview" style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10, margin: '8px 0', background: '#fff' }}
                    dangerouslySetInnerHTML={{ __html: previewSvg }} />
            )}

            <div className="ace-form-actions">
                <button onClick={handleSave} disabled={saving}>{saving ? 'Rendering & Saving...' : 'Save'}</button>
                <button onClick={onCancel} className="ace-cancel-btn">Cancel</button>
            </div>
        </div>
    );
};

export default FlowchartForm;